import { createProgramFromSources } from "./twglish";

// default colors of and area of each region
// are the openCv contour indices + 1, leaving 0 for the background
export let imageMetadata: { colors: [string, number[]][] } = {
  colors: [
    ["255,255,255", [93, 117]],
    ["195,139,124", [1, 5, 41, 42]],
    ["97,102,194", [56, 111, 112]],
    ["254,198,142", [30, 61, 77, 90, 94]],
    ["178,181,229", [62]],
    ["135,174,229", [107, 108]],
    ["255,242,56", [65, 85, 87]],
    ["143,129,125", [4, 31, 57]],
    ["140,199,75", [97]],
    ["184,230,250", [69, 103]],
    ["201,177,61", [7, 8, 11, 12, 23, 24, 32, 33, 34, 40, 43, 44, 45, 2, 6]],
    ["245,155,193", [84, 96]],
    ["133,174,232", [115, 116]],
    ["203,219,65", [22, 35, 51, 72, 81, 91, 92]],
    ["195,180,141", [18]],
    ["195,139,124", [25, 53, 54, 55, 64, 68]],
    ["255,194,49", [50, 52, 60, 58, 59, 71, 73, 74, 78, 80, 82, 83]],
    ["254,193,49", [13, 15, 27, 36, 38, 20]],
    ["250,148,47", [46]],
    ["255,248,160", [49]],
    ["253,253,253", [102]],
    ["253,240,56", [17, 9, 10, 26, 29, 19]],
  ],
};

const vertexShader = `
attribute vec2 a_position;
attribute vec2 a_texCoord;
uniform vec2 u_resolution;
varying vec2 v_texCoord;
void main() {
vec2 zeroToOne = a_position / u_resolution;
vec2 zeroToTwo = zeroToOne * 2.0;
vec2 clipSpace = zeroToTwo - 1.0;
gl_Position = vec4(clipSpace * vec2(1, -1), 0, 1);
v_texCoord = a_texCoord;
}
`;

const fragmentShader = `
precision mediump float;
uniform sampler2D u_image;
uniform sampler2D u_palette;
varying vec2 v_texCoord;
void main() {
gl_FragColor = texture2D(u_palette, vec2(texture2D(u_image, v_texCoord).x, 0.0));
}
`;

export function render(
  image: HTMLImageElement,
  canvas: HTMLCanvasElement,
  majorColors: number[][]
) {
  // Get A WebGL context
  var gl = canvas.getContext("webgl");
  if (!gl) {
    return;
  }

  // setup GLSL program
  var program = createProgramFromSources(gl, [vertexShader, fragmentShader]);

  // look up where the vertex data needs to go.
  var positionLocation = gl.getAttribLocation(program, "a_position");
  var texcoordLocation = gl.getAttribLocation(program, "a_texCoord");

  // Create a buffer to put three 2d clip space points in
  var positionBuffer = gl.createBuffer();

  // Bind it to ARRAY_BUFFER (think of it as ARRAY_BUFFER = positionBuffer)
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  // Set a rectangle the same size as the image.
  setRectangle(gl, 0, 0, image.width, image.height);

  // provide texture coordinates for the rectangle.
  var texcoordBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, texcoordBuffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([
      0.0,
      0.0,
      1.0,
      0.0,
      0.0,
      1.0,
      0.0,
      1.0,
      1.0,
      0.0,
      1.0,
      1.0,
    ]),
    gl.STATIC_DRAW
  );

  // Create a texture.
  var texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);

  // Set the parameters so we can render any size image.
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);

  // Upload the image into the texture.
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);

  // palette to apply to texture
  var colors = createPalette(majorColors);
  var palette = gl.createTexture();

  gl.bindTexture(gl.TEXTURE_2D, palette);
  // Set the parameters so we can render any size image (palette ought to be a power of 2 though 256x1)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);

  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.RGB,
    256,
    1,
    0,
    gl.RGB,
    gl.UNSIGNED_BYTE,
    colors
  );

  // lookup uniforms
  var resolutionLocation = gl.getUniformLocation(program, "u_resolution");
  var textureSizeLocation = gl.getUniformLocation(program, "u_textureSize");

  // Tell WebGL how to convert from clip space to pixels
  gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);

  // Clear the canvas
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);

  // Tell it to use our program (pair of shaders)
  gl.useProgram(program);

  // Turn on the position attribute
  gl.enableVertexAttribArray(positionLocation);

  // Bind the position buffer.
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);

  // Tell the position attribute how to get data out of positionBuffer (ARRAY_BUFFER)
  var size = 2; // 2 components per iteration
  var type = gl.FLOAT; // the data is 32bit floats
  var normalize = false; // don't normalize the data
  var stride = 0; // 0 = move forward size * sizeof(type) each iteration to get the next position
  var offset = 0; // start at the beginning of the buffer
  gl.vertexAttribPointer(
    positionLocation,
    size,
    type,
    normalize,
    stride,
    offset
  );

  // Turn on the teccord attribute
  gl.enableVertexAttribArray(texcoordLocation);

  // Bind the position buffer.
  gl.bindBuffer(gl.ARRAY_BUFFER, texcoordBuffer);

  // Tell the position attribute how to get data out of positionBuffer (ARRAY_BUFFER)
  var size = 2; // 2 components per iteration
  var type = gl.FLOAT; // the data is 32bit floats
  var normalize = false; // don't normalize the data
  var stride = 0; // 0 = move forward size * sizeof(type) each iteration to get the next position
  var offset = 0; // start at the beginning of the buffer
  gl.vertexAttribPointer(
    texcoordLocation,
    size,
    type,
    normalize,
    stride,
    offset
  );

  // set the resolution (larger numbers make it smaller)
  gl.uniform2f(resolutionLocation, gl.canvas.width * 2, gl.canvas.height * 2);

  // set the size of the image
  gl.uniform2f(textureSizeLocation, image.width, image.height);

  // use two textures, image and palette
  // lookup the sampler locations.
  var u_imageLocation = gl.getUniformLocation(program, "u_image");
  var u_paletteLocation = gl.getUniformLocation(program, "u_palette");

  // set which texture units to render with.
  gl.uniform1i(u_imageLocation, 0); // texture unit 0
  gl.uniform1i(u_paletteLocation, 1); // texture unit 1

  // Set each texture unit to use a particular texture.
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, palette);

  // Draw the rectangle.
  var primitiveType = gl.TRIANGLES;
  var offset = 0;
  var count = 6;
  gl.drawArrays(primitiveType, offset, count);
}

function setRectangle(
  gl: WebGLRenderingContext,
  x: number,
  y: number,
  width: number,
  height: number
) {
  var x1 = x;
  var x2 = x + width;
  var y1 = y;
  var y2 = y + height;
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([x1, y1, x2, y1, x1, y2, x1, y2, x2, y1, x2, y2]),
    gl.STATIC_DRAW
  );
}

function createPalette(colors: number[][]) {
  var paletteLength = 256;
  var paletteHeight = 1;
  var paletteChannels = 3;

  var temp = new Uint8Array(paletteLength * paletteHeight * paletteChannels);
  colors.forEach((color, index) => {
    let regions: number[] = imageMetadata.colors[index][1] as any;
    regions.forEach(region => {
      let index = region * paletteChannels;
      temp[index] = color[0];
      temp[index + 1] = color[1];
      temp[index + 2] = color[2];
    });
  });

  return temp;
}
